import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';

interface CardProps extends HTMLAttributes<HTMLElement> {
  /** Supprime la marge intérieure (listes pleine largeur, tableaux). */
  flush?: boolean;
  /** Ombre renforcée au survol (carte cliquable). */
  interactive?: boolean;
  as?: 'section' | 'div' | 'article';
}

/** Carte blanche, arrondi 24, ombre douce multicouche. */
export function Card({ flush = false, interactive = false, as: Tag = 'section', className, ...props }: CardProps) {
  return (
    <Tag
      className={cx(
        'min-w-0 rounded-card bg-white shadow-card',
        !flush && 'p-6 max-sm:p-[18px]',
        interactive && 'transition-shadow duration-300 hover:shadow-card-hover',
        className,
      )}
      {...props}
    />
  );
}

interface CardHeaderProps {
  /** Petit titre en majuscules (information). */
  eyebrow?: ReactNode;
  /** Balise du petit titre (h2 quand il n'y a pas de `title`). */
  eyebrowAs?: 'p' | 'h2' | 'h3';
  title?: ReactNode;
  description?: ReactNode;
  /** Actions alignées à droite (boutons `sm`). */
  actions?: ReactNode;
  className?: string;
}

export function CardHeader({ eyebrow, eyebrowAs, title, description, actions, className }: CardHeaderProps) {
  return (
    <header className={cx('mb-[18px] flex min-h-8 items-center gap-2.5', className)}>
      <div className="min-w-0 flex-1">
        {eyebrow && <Eyebrow as={eyebrowAs ?? (title ? 'p' : 'h2')}>{eyebrow}</Eyebrow>}
        {title && <h3 className="text-title-sm text-heading">{title}</h3>}
        {description && <p className="mt-0.5 text-[13px] leading-normal text-secondary">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
    </header>
  );
}

/**
 * Petit titre de bloc : 11px, 700, majuscules, interlettrage 1px.
 * `as="h2"` quand il est le seul titre du bloc (structure de page accessible).
 */
export function Eyebrow({
  children,
  className,
  as: Tag = 'p',
}: {
  children: ReactNode;
  className?: string;
  as?: 'p' | 'h2' | 'h3';
}) {
  return <Tag className={cx('text-eyebrow uppercase text-muted', className)}>{children}</Tag>;
}

interface SectionHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}

/** En-tête de section de page (titre 24px + actions). */
export function SectionHeader({ title, description, actions }: SectionHeaderProps) {
  return (
    <div className="mb-5 flex flex-wrap items-center gap-3">
      <h2 className="text-title-md text-heading">{title}</h2>
      {actions && <div className="ml-auto flex flex-wrap gap-2">{actions}</div>}
      {description && <p className="-mt-1.5 w-full text-sm text-secondary">{description}</p>}
    </div>
  );
}
